import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCartShopping } from '@fortawesome/free-solid-svg-icons'
import { shopApi } from '../lib/api'

export function HeaderCartButton() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const cartQ = useQuery({
    queryKey: ['cart'],
    queryFn: shopApi.getCart,
    staleTime: 15_000,
  })

  const cart = cartQ.data?.data
  const itemCount = cart?.items.reduce((n, i) => n + i.quantity, 0) ?? 0

  const qtyM = useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) => {
      if (quantity < 1) return shopApi.removeItem(id)
      return shopApi.updateQuantity(id, quantity)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cart'] }),
  })

  const removeM = useMutation({
    mutationFn: shopApi.removeItem,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cart'] }),
  })

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={`header-cart ${open ? 'is-open' : ''}`} ref={ref}>
      <button
        type="button"
        className="icon-btn header-cart-btn"
        aria-label={t('shop.cart')}
        title={t('shop.cart')}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <FontAwesomeIcon icon={faCartShopping} />
        {itemCount > 0 ? <span className="header-cart-badge">{itemCount > 99 ? '99+' : itemCount}</span> : null}
      </button>

      {open && (
        <div className="header-cart-panel" role="dialog" aria-label={t('shop.cart')}>
          <div className="header-cart-head">
            <strong>{t('shop.cart')}</strong>
            {itemCount > 0 ? <span>{t('shop.cartItems', { count: itemCount })}</span> : null}
          </div>

          {!cart?.items.length && <p className="header-cart-empty">{t('shop.cartEmpty')}</p>}

          <div className="header-cart-list">
            {cart?.items.map((item) => (
              <article key={item.id} className="header-cart-row">
                <div className="header-cart-info">
                  <strong>{item.name}</strong>
                  <span>
                    {item.unitPrice} €
                    {item.pointsCost ? ` · ${item.pointsCost} ${t('shop.pointsShort')}` : ''}
                  </span>
                </div>
                <div className="header-cart-qty">
                  <button
                    type="button"
                    aria-label="−"
                    disabled={qtyM.isPending || removeM.isPending}
                    onClick={() => qtyM.mutate({ id: item.id, quantity: item.quantity - 1 })}
                  >
                    −
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    aria-label="+"
                    disabled={qtyM.isPending || removeM.isPending || item.type === 'membership'}
                    onClick={() => qtyM.mutate({ id: item.id, quantity: item.quantity + 1 })}
                  >
                    +
                  </button>
                </div>
              </article>
            ))}
          </div>

          {!!cart?.items.length && (
            <div className="header-cart-foot">
              <p>
                <strong>
                  {t('shop.total')}: {cart.total} €
                </strong>
              </p>
              <Link className="btn btn-primary" to="/shop?tab=cart" onClick={() => setOpen(false)}>
                {t('shop.openCart')}
              </Link>
              <Link className="btn btn-ghost" to="/shop?tab=checkout" onClick={() => setOpen(false)}>
                {t('shop.checkout')}
              </Link>
            </div>
          )}

          {!cart?.items.length && (
            <Link className="btn btn-ghost header-cart-shop" to="/shop" onClick={() => setOpen(false)}>
              {t('nav.shop')}
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
